import { sfUrl } from '../StorefrontPaths';
import { BotGuard } from '../../BotProtection/BotGuard';
/**
 * Storefront sign-in: passwordless magic link (default) or optional password.
 * Magic mode posts to RequestMagicLink (always generic response); password mode
 * posts to Login and redirects to the account on success.
 * #37115: only magic mode carries the bot check (invisible Turnstile, loaded the first time
 * that tab opens); password sign-in is rate limited server-side, with no captcha.
 */
type Mode = "magic" | "password";

class Auth {
    private root: HTMLElement;
    private form: HTMLFormElement;
    private email: HTMLInputElement;
    private passWrap: HTMLElement;
    private pass: HTMLInputElement;
    private passToggle: HTMLButtonElement;
    private submit: HTMLButtonElement;
    private msg: HTMLElement;
    private magicGuardWrap: HTMLElement | null;
    private magicGuard: BotGuard | null = null;
    private mode: Mode = "password";
    private busy = false;

    constructor(root: HTMLElement) {
        this.root = root;
        this.form = root.querySelector("#sf-auth-form") as HTMLFormElement;
        this.email = root.querySelector("#sf-auth-email") as HTMLInputElement;
        this.passWrap = root.querySelector("#sf-auth-pass-wrap") as HTMLElement;
        this.pass = root.querySelector("#sf-auth-pass") as HTMLInputElement;
        this.passToggle = root.querySelector("#sf-auth-pass-toggle") as HTMLButtonElement;
        this.submit = root.querySelector("#sf-auth-submit") as HTMLButtonElement;
        this.msg = root.querySelector('[data-region="msg"]') as HTMLElement;
        this.magicGuardWrap = root.querySelector("#sf-auth-magic-guard") as HTMLElement | null;

        root.querySelectorAll<HTMLButtonElement>(".sf-auth-tab").forEach(tab =>
            tab.addEventListener("click", () => this.setMode(tab.dataset.mode as Mode)));
        this.form.addEventListener("submit", e => { e.preventDefault(); this.go(); });
        this.passToggle?.addEventListener("click", () => this.togglePasswordVisibility());

        // "New customer?" email-first step → registration page (email carried over).
        const regForm = root.querySelector("#sf-reg-start") as HTMLFormElement | null;
        regForm?.addEventListener("submit", e => {
            e.preventDefault();
            const email = (root.querySelector("#sf-reg-start-email") as HTMLInputElement)?.value?.trim() || "";
            const q = email ? `?email=${encodeURIComponent(email)}` : "";
            location.href = `${sfUrl('/account/register')}${q}`;
        });

        if (new URLSearchParams(location.search).get("expired"))
            this.show("That sign-in link expired. Enter your email for a new one.", "warn");
    }

    private setMode(mode: Mode): void {
        if (!mode || mode === this.mode) return;
        this.mode = mode;
        this.root.querySelectorAll<HTMLButtonElement>(".sf-auth-tab").forEach(t =>
            t.classList.toggle("is-active", t.dataset.mode === mode));
        const pw = mode === "password";
        this.passWrap.hidden = !pw;
        this.pass.required = pw;
        this.submit.textContent = pw ? "Sign in" : "Email me a sign-in link";
        this.msg.textContent = "";
        if (this.magicGuardWrap) this.magicGuardWrap.hidden = pw;
        // Render the widget only once the magic tab is actually used (password users never load it).
        if (!pw && !this.magicGuard) this.magicGuard = BotGuard.forForm("magiclink", this.root);
    }

    private togglePasswordVisibility(): void {
        const show = this.pass.type === "password";
        this.pass.type = show ? "text" : "password";
        this.passToggle.setAttribute("aria-pressed", String(show));
        this.passToggle.setAttribute("aria-label", show ? "Hide password" : "Show password");
        const icon = this.passToggle.querySelector("i")!;
        icon.classList.toggle("fa-eye", !show);
        icon.classList.toggle("fa-eye-slash", show);
    }

    private async go(): Promise<void> {
        if (this.busy) return;
        const email = this.email.value.trim();
        if (!email) return;
        this.busy = true;
        this.submit.disabled = true;
        const label = this.submit.textContent;
        this.submit.textContent = "Please wait…";
        this.msg.textContent = "";

        try {
            if (this.mode === "magic") {
                if (!this.magicGuard) this.magicGuard = BotGuard.forForm("magiclink", this.root);
                const guard = this.magicGuard;
                let res: Response;
                try {
                    res = await fetch("/Services/Storefront/Account/RequestMagicLink", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", ...(await guard.getHeaders()) },
                        body: JSON.stringify({ Email: email })
                    });
                } finally {
                    // Turnstile tokens are single-use: get a fresh one for the next request.
                    guard.reset();
                }
                if (!res.ok) {
                    // Bot check failed (400) or rate limited (429): say so; the email is never revealed.
                    const data = await res.json().catch(() => null);
                    this.show((data && (data.Error?.Message || data.error)) || "Something went wrong. Please try again.", "err");
                    return;
                }
                this.show("If an account exists for that email, we've sent a sign-in link. Check your inbox.", "ok");
                this.form.reset();
            } else {
                const res = await fetch("/Services/Storefront/Account/Login", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Email: email, Password: this.pass.value })
                });
                if (res.ok) { location.href = sfUrl('/account'); return; }
                const data = await res.json().catch(() => null);
                this.show((data && (data.Error?.Message || data.error)) || "Invalid email or password.", "err");
            }
        } catch {
            this.show("Something went wrong. Please try again.", "err");
        } finally {
            this.busy = false;
            this.submit.disabled = false;
            this.submit.textContent = label;
        }
    }

    private show(text: string, kind: "ok" | "err" | "warn"): void {
        this.msg.textContent = text;
        this.msg.className = `sf-auth-msg ${kind}`;
    }
}

function init(): void {
    const root = document.getElementById("sf-auth-root");
    if (root) new Auth(root);
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
